import { Input, Switch, Tooltip } from "antd";
import { message } from "utils/ciq-message";
import { useEffect, useMemo, useState } from "react";
import debounce from "lodash.debounce";

export function matchProjectFeatures(search: string, features: string[]) {
  const query = search.trim().toLowerCase();
  if (!query) return features;
  return features.filter((feature) =>
    feature.replaceAll("_", " ").toLowerCase().includes(query)
  );
}

export function ColumnGroupSearchHeader({
  displayName,
  getSearchValue,
  onSearchChange,
  placeholder = "Search",
  searchAriaLabel = "Search",
  inputClassName = "max-w-[180px] min-w-[100px]",
  matchItems
}: {
  displayName?: string;
  getSearchValue?: () => string;
  onSearchChange?: (value: string) => void;
  placeholder?: string;
  searchAriaLabel?: string;
  inputClassName?: string;
  matchItems?: string[];
}) {
  const [value, setValue] = useState(() => getSearchValue?.() ?? "");

  const debouncedSearchChange = useMemo(
    () =>
      debounce((text: string) => {
        onSearchChange?.(text);
      }, 500),
    [onSearchChange]
  );

  useEffect(() => {
    return () => {
      debouncedSearchChange.cancel();
    };
  }, [debouncedSearchChange]);

  const stopGridEvent = (event: { stopPropagation?: () => void }) => {
    event.stopPropagation?.();
  };

  const matchCount =
    matchItems && value.trim()
      ? matchProjectFeatures(value, matchItems).length
      : null;

  return (
    <div className="flex items-center gap-2 w-full min-w-0 h-full">
      <span className="whitespace-nowrap shrink-0">{displayName}</span>
      <Input
        size="small"
        allowClear
        value={value}
        placeholder={placeholder}
        aria-label={searchAriaLabel}
        className={inputClassName}
        onClick={stopGridEvent}
        onMouseDown={stopGridEvent}
        onKeyDown={stopGridEvent}
        onChange={(event) => {
          const next = event.target.value;
          setValue(next);
          debouncedSearchChange(next);
        }}
      />
      {matchCount != null && matchItems ? (
        <span
          className="text-xs text-black/45 whitespace-nowrap shrink-0"
          aria-live="polite"
        >
          {matchCount} / {matchItems.length}
        </span>
      ) : null}
    </div>
  );
}

/** @deprecated Prefer ColumnGroupSearchHeader — kept as a named alias for call sites. */
export const ProjectLevelFeaturesInnerHeader = ColumnGroupSearchHeader;

// Cell renderer for subscription-level switches
export function SubscriptionSwitchCellRenderer({
  value,
  data: rowData,
  context,
  colDef,
  node
}: any) {
  const [loading, setLoading] = useState(false);

  const handleChange = async (checked: boolean) => {
    const feature = colDef?.field?.split(".")[0];

    if (!feature || !rowData.id) return;

    // Find the product entitlement ID for this feature
    const featureFlag = rowData.subscription_product_entitlements?.find(
      (e: any) => e.product_entitlement?.feature === feature
    );

    if (!featureFlag) {
      return;
    }

    setLoading(true);
    try {
      const res = await context.updateSubscriptionFeatureFlag({
        subscriptionId: rowData.id,
        featureFlagId: featureFlag.product_entitlement.id,
        featureFlag: checked
      });
      if (res?.data?.update_feature_flag?.message) {
        message.success(res.data.update_feature_flag.message);
        node?.setDataValue(colDef.field, checked);
      }
    } catch {
      message.error("Failed to update feature flag");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Switch
      defaultChecked={value}
      disabled={loading}
      onChange={handleChange}
      loading={loading}
      size="small"
      unCheckedChildren="OFF"
      checkedChildren="ON"
    />
  );
}

// Column-header toggle for the subscription-level "enable for future projects"
// flag of a single feature. Rendered in place of the "Enable/Disable" header
// text in the project detail grid; the parent feature column's product
// entitlement id is sent as the feature flag id.
export function FutureProjectsHeaderToggle({
  subscriptionId,
  featureFlagId,
  initialValue,
  onToggle
}: any) {
  const [checked, setChecked] = useState<boolean>(!!initialValue);
  const [loading, setLoading] = useState(false);

  // Keep in sync if the grid re-seeds the header with a new value.
  useEffect(() => {
    setChecked(!!initialValue);
  }, [initialValue]);

  const handleChange = async (value: boolean) => {
    if (!subscriptionId || featureFlagId == null) return;

    setLoading(true);
    try {
      const res = await onToggle({
        subscriptionId,
        featureFlagId,
        enableForFutureProjects: value
      });
      if (res?.data?.update_feature_flag?.message) {
        message.success(res.data.update_feature_flag.message);
        setChecked(value);
      } else {
        // No row matched (e.g. it was changed/removed elsewhere): surface it
        // instead of letting the controlled switch silently snap back.
        message.error("Failed to update feature flag");
      }
    } catch {
      message.error("Failed to update feature flag");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Tooltip title="Enable for Future/New Projects">
      <Switch
        checked={checked}
        disabled={loading || featureFlagId == null}
        onChange={handleChange}
        loading={loading}
        size="small"
        unCheckedChildren="OFF"
        checkedChildren="ON"
      />
    </Tooltip>
  );
}

// Cell renderer for project-level switches
export function ProjectSwitchCellRenderer({
  value,
  data: rowData,
  context,
  colDef,
  node,
  readonly
}: any) {
  const [loading, setLoading] = useState(false);

  const handleChange = async (checked: boolean) => {
    // Unreachable: the Switch is disabled when readonly, so onChange (->
    // handleChange) never fires while readonly is true.
    /* v8 ignore next */
    if (readonly) return;

    const feature = colDef?.field?.split(".")[0];
    if (!feature || !rowData.id) return;

    const featureFlag = rowData.project_product_entitlements?.find(
      (e: any) => e.product_entitlement?.feature === feature
    );

    if (!featureFlag) {
      return;
    }

    setLoading(true);
    try {
      const variables: any = {
        projectId: rowData.id,
        featureFlagId: featureFlag.product_entitlement.id,
        featureFlag: checked
      };

      const res = await context.updateProjectFeatureFlag(variables);

      if (res?.data?.update_feature_flag?.message) {
        message.success(res.data.update_feature_flag.message);
        node?.setDataValue(colDef.field, checked);
      }
    } catch {
      message.error("Failed to update project feature flag");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Switch
      defaultChecked={value}
      disabled={loading || readonly}
      onChange={handleChange}
      loading={loading}
      size="small"
      unCheckedChildren="OFF"
      checkedChildren="ON"
    />
  );
}
